import React, { useState, useEffect } from 'react';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import '../Payment/sportpayment.css';

const SportPayment = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [payments, setPayments] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('auth-token');

  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(sevenDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchPayments = async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SPORTWISE_REPORT}?startDate=${startDate}&endDate=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payments');
      }

      const data = await response.json();

      if (data.success && data.data.apiData.length > 0) {
        const firstItem = data.data.apiData[0];

        const dynamicHeaders = Object.keys(firstItem)
          .filter((key) => key !== 'Date')
          .map((key) => ({ key, label: key }));

        setHeaders(dynamicHeaders);

        setPayments(
          data.data.apiData.map((payment, index) => ({
            id: index + 1,
            ...payment,
            date: payment.Date || startDate,
          }))
        );
      } else {
        throw new Error('Error fetching data');
      }
    } catch (err) {
      setError(err.message);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [startDate, endDate]);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleItemsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (event) => setStartDate(event.target.value);
  const handleEndDateChange = (event) => setEndDate(event.target.value);

  const filteredPayments = payments.filter((payment) =>
    Object.values(payment)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);

  const handleSort = (columnKey) => {
    let direction = 'ascending';
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    if (!key) return data;
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredPayments);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };
  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () =>
    generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );

  const totalEntries = filteredPayments.length;
  const indexOfFirstEntry =
    totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry =
    totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">
      {/* Date Filters */}
      <div className="sales-filters-login">
        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${startDate ? 'has-value' : ''}`}
            value={startDate}
            onChange={handleStartDateChange}
            max={new Date().toISOString().split('T')[0]}
          />
          {startDate && <label>Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy ${endDate ? 'has-value' : ''}`}
            value={endDate}
            onChange={handleEndDateChange}
            max={new Date().toISOString().split('T')[0]}
          />
          {endDate && <label>End Date</label>}
        </div>
      </div>

      {/* Search */}
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            className={`search-input ${searchTerm ? 'has-value' : ''}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
      </div>

      {/* Items per page + Refresh */}
      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchPayments}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loader */}
      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}

      {/* Errors */}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredPayments.length === 0 && (
        <div className="error-message">No data available</div>
      )}

        <div className="tt-table-container">
          <table className="tt-table">
            <thead>
              <tr>
                {headers.map(({ key, label }) => (
                  <th key={key} onClick={() => handleSort(key)}>
                    <div className="th-content">
                      <span>{label}</span>
                      <FiChevronDown
                        className={`sort-icon ${
                          sortConfig.key === key ? 'active' : ''
                        } ${
                          sortConfig.key === key &&
                          sortConfig.direction === 'descending'
                            ? 'desc'
                            : ''
                        }`}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentRows.map((payment) => (
                <tr key={payment.id}>
                  {headers.map(({ key }) => (
                    <td key={key}>{payment[key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      {/* Pagination */}
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries}{' '}
          entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SportPayment;
